import React, { useState, useContext } from "react"
import {
  Modal,
  List,
  Tag,
  Typography,
  Space,
  Button,
  Radio,
  Badge,
  Spin,
  Empty,
  message,
  InputNumber,
  Switch,
  Tooltip,
} from "antd"
import { Bell, DollarSign, Package, ExternalLink, RefreshCw, Settings, CheckCircle } from "react-feather"
import { useHistory } from "react-router-dom"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import {
  GET_NOTIFICATIONS,
  MARK_NOTIFICATION_READ,
  MARK_ALL_NOTIFICATIONS_READ,
  GET_ALERT_SETTINGS,
  UPDATE_ALERT_SETTINGS,
  REFRESH_ALERTS,
} from "../../rest-api/queries/notifications"
import { ThemeContext } from "../../contexts/ThemeContex"
import { AuthContext } from "../../contexts/auth/AuthContext"
import { useTranslation } from "react-i18next"

const DEBT_TYPES = ["debt_alert", "debt_limit_alert"]
const STOCK_TYPES = ["low_stock_alert", "out_of_stock"]

export default function NotificationsModal({
  visible,
  onClose,
}: {
  visible: boolean
  onClose: () => void
}) {
  const history = useHistory()
  const { themeState } = useContext(ThemeContext)
  const { authState } = useContext(AuthContext)
  const isDark = themeState.currentTheme === "dark"
  const isSuperAdmin = authState.user?.userName === "Administrator"
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  // Qo'ng'iroqcha ustidagi son alohida so'rovdan keladi — o'qilgan deb
  // belgilangandan keyin u ham darhol yangilanishi kerak
  const refreshBadge = () =>
    queryClient.invalidateQueries({ queryKey: ["GET_NOTIFICATIONS_BADGE"] })

  // Toifali filtr: faqat 2 ta bo'lim qoldirildi — "Barchasi", "O'qilmagan" va
  // "Vazifalar" tablari guruh talabiga ko'ra olib tashlandi
  const [filterType, setFilterType] = useState<string>("debt")
  const [showSettings, setShowSettings] = useState(false)

  const category = filterType === "stock" ? "stock" : "debt"

  const {
    data: notifsData,
    isLoading: loading,
    refetch: refetchNotifs,
  } = useQuery(
    ["GET_NOTIFICATIONS", category],
    () => GET_NOTIFICATIONS({ unreadOnly: false, category, page: 1, pageSize: 100 }),
    { enabled: visible }
  )

  const list: any[] = notifsData?.data || notifsData?.Data || []
  const unreadCount = notifsData?.unreadCount ?? notifsData?.UnreadCount ?? 0

  // Yuqoridagi son faqat ochiq bo'lim bo'yicha. "Barchasini o'qildi" esa ikkala
  // bo'limni tozalaydi, shuning uchun tugma umumiy son bo'yicha faollashadi.
  // Kalit qo'ng'iroqchanikiga teng — react-query so'rovni takrorlamaydi.
  const { data: badgeData } = useQuery(
    ["GET_NOTIFICATIONS_BADGE"],
    () => GET_NOTIFICATIONS({ unreadOnly: true, page: 1, pageSize: 1 }),
    { enabled: visible }
  )
  const totalUnread = badgeData?.unreadCount ?? badgeData?.UnreadCount ?? 0

  // Chegaralar (qarz kunlari/summasi, minimal qoldiq)
  const { data: settings, refetch: refetchSettings } = useQuery(
    ["GET_ALERT_SETTINGS"],
    GET_ALERT_SETTINGS,
    { enabled: visible && isSuperAdmin }
  )
  const [draft, setDraft] = useState<any>(null)
  const current = draft || settings

  const { mutate: saveSettings, isLoading: savingSettings } = useMutation(
    UPDATE_ALERT_SETTINGS,
    {
      onSuccess: () => {
        message.success(t("limitsSaved"))
        setDraft(null)
        refetchSettings()
        refetchNotifs()
      },
      onError: (err: any) =>
        message.error(t("errSave") + ": " + (err?.response?.data?.Message || err.message)),
    }
  )

  const { mutate: refreshAlerts, isLoading: refreshing } = useMutation(REFRESH_ALERTS, {
    onSuccess: (res: any) => {
      const created = res?.created ?? res?.Created ?? 0
      message.success(
        created > 0 ? `${created} ${t("newAlertsFound")}` : String(t("noNewAlerts"))
      )
      refetchNotifs()
    },
    onError: (err: any) =>
      message.error(t("errRefresh") + ": " + (err?.response?.data?.Message || err.message)),
  })

  const { mutate: markReadMutation } = useMutation(MARK_NOTIFICATION_READ, {
    onSuccess: () => {
      refetchNotifs()
      refreshBadge()
    },
  })

  const { mutate: markAllRead, isLoading: markingAll } = useMutation(
    () => MARK_ALL_NOTIFICATIONS_READ(),
    {
      onSuccess: (res: any) => {
        const count = res?.updatedCount ?? res?.UpdatedCount ?? 0
        message.success(
          count > 0 ? `${count} ${t("markedAsRead")}` : String(t("noUnreadAlerts"))
        )
        refetchNotifs()
        refreshBadge()
      },
      onError: (err: any) =>
        message.error(t("errSave") + ": " + (err?.response?.data?.Message || err.message)),
    }
  )

  const handleNavigate = (item: any) => {
    const url = item.linkUrl ?? item.LinkUrl
    if (!url) return
    // Eslatma ustiga bosilganda o'qilgan deb belgilanadi va kerakli kartaga o'tiladi
    if (!(item.isRead ?? item.IsRead)) markReadMutation(item.id ?? item.Id)
    onClose()
    history.push(url)
  }

  const getIcon = (type: string) => {
    if (DEBT_TYPES.includes(type)) return <DollarSign size={18} color="#EF4444" />
    if (type === "out_of_stock") return <Package size={18} color="#EF4444" />
    if (STOCK_TYPES.includes(type)) return <Package size={18} color="#F59E0B" />
    return <Bell size={18} color="#6366F1" />
  }

  const isCritical = (type: string) =>
    type === "out_of_stock" || DEBT_TYPES.includes(type)

  return (
    <Modal
      title={
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
            paddingRight: 20,
          }}
        >
          <Space align="center">
            <Bell size={20} color="#2563EB" />
            <Typography.Title level={5} style={{ margin: 0 }}>
              {isSuperAdmin ? t("alertsTitle") : t("notifications")}
            </Typography.Title>
            {unreadCount > 0 && (
              <Badge count={unreadCount} style={{ backgroundColor: "#EF4444" }} />
            )}
          </Space>
          <Space size={4}>
            {isSuperAdmin && (
              <>
                <Tooltip title={t("recalcAlerts")}>
                  <Button
                    type="text"
                    size="small"
                    loading={refreshing}
                    icon={<RefreshCw size={14} />}
                    onClick={() => refreshAlerts()}
                  />
                </Tooltip>
                <Tooltip title={t("configureLimits")}>
                  <Button
                    type="text"
                    size="small"
                    icon={<Settings size={14} />}
                    onClick={() => setShowSettings((v) => !v)}
                  />
                </Tooltip>
              </>
            )}
          </Space>
        </div>
      }
      visible={visible}
      onCancel={onClose}
      footer={null}
      width={680}
      bodyStyle={{ padding: "16px 20px" }}
      centered
      destroyOnClose
    >
      {/* Chegaralar paneli */}
      {isSuperAdmin && showSettings && current && (
        <div
          style={{
            padding: "12px 14px",
            marginBottom: 14,
            borderRadius: 10,
            backgroundColor: isDark ? "#1E182A" : "#F8FAFC",
            border: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid #E2E8F0",
          }}
        >
          <Typography.Text strong style={{ fontSize: 13, display: "block", marginBottom: 8 }}>
            {t("alertLimits")}
          </Typography.Text>
          <div style={{ display: "flex", gap: 14, flexWrap: "wrap", alignItems: "flex-end" }}>
            <div>
              <div style={{ fontSize: 11, color: "#64748B" }}>{t("paymentTermDays")}</div>
              <InputNumber
                size="small"
                min={1}
                value={current.maxDebtDays}
                onChange={(v) => setDraft({ ...current, maxDebtDays: v || 1 })}
                style={{ width: 120 }}
              />
            </div>
            <div>
              <div style={{ fontSize: 11, color: "#64748B" }}>{t("debtLimitUsd")}</div>
              <InputNumber
                size="small"
                min={0}
                value={current.maxDebtAmountUSD}
                onChange={(v) => setDraft({ ...current, maxDebtAmountUSD: v || 0 })}
                style={{ width: 150 }}
              />
            </div>
            <div>
              <div style={{ fontSize: 11, color: "#64748B" }}>{t("defaultMinStockLabel")}</div>
              <InputNumber
                size="small"
                min={0}
                value={current.defaultMinStock}
                onChange={(v) => setDraft({ ...current, defaultMinStock: v || 0 })}
                style={{ width: 140 }}
              />
            </div>
            <div>
              <div style={{ fontSize: 11, color: "#64748B" }}>{t("enabledLabel")}</div>
              <Switch
                size="small"
                checked={current.enabled}
                onChange={(v) => setDraft({ ...current, enabled: v })}
              />
            </div>
            <Button
              type="primary"
              size="small"
              loading={savingSettings}
              disabled={!draft}
              onClick={() => saveSettings(current)}
              style={{ borderRadius: 6 }}
            >
              {t("save")}
            </Button>
          </div>
          <Typography.Text type="secondary" style={{ fontSize: 11, display: "block", marginTop: 6 }}>
            {t("perProductLimitHint")}
          </Typography.Text>
        </div>
      )}

      {/* Toifali filtr — faqat 2 ta bo'lim */}
      <div
        style={{
          marginBottom: 14,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 8,
          flexWrap: "wrap",
        }}
      >
        <Radio.Group
          size="small"
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
        >
          <Radio.Button value="debt">{t("debtsTab")}</Radio.Button>
          <Radio.Button value="stock">{t("stockTab")}</Radio.Button>
        </Radio.Group>

        {/* Ikkala bo'limdagi o'qilmagan eslatmalarni birdan yopish */}
        <Button
          size="small"
          icon={<CheckCircle size={13} style={{ marginRight: 6 }} />}
          loading={markingAll}
          disabled={totalUnread === 0}
          onClick={() => markAllRead()}
          style={{ display: "inline-flex", alignItems: "center", borderRadius: 6 }}
        >
          {t("markAllRead")}
          {totalUnread > 0 ? ` (${totalUnread})` : ""}
        </Button>
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: 40 }}>
          <Spin />
        </div>
      ) : list.length === 0 ? (
        <Empty description={t("noAlerts")} />
      ) : (
        <List
          itemLayout="vertical"
          dataSource={list}
          style={{ maxHeight: 420, overflowY: "auto" }}
          renderItem={(item: any) => {
            const id = item.id ?? item.Id
            const type = item.type ?? item.Type ?? "system"
            const isRead = item.isRead ?? item.IsRead
            const createdAt = item.createdAt ?? item.CreatedAt
            const linkUrl = item.linkUrl ?? item.LinkUrl
            const timeStr = createdAt
              ? new Date(createdAt).toLocaleDateString([], {
                  month: "short",
                  day: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                })
              : String(t("recently"))

            return (
              <div
                key={id}
                onClick={() => handleNavigate(item)}
                style={{
                  display: "flex",
                  gap: 12,
                  padding: "12px 14px",
                  marginBottom: 8,
                  borderRadius: 10,
                  cursor: linkUrl ? "pointer" : "default",
                  backgroundColor: isRead
                    ? isDark
                      ? "#1E182A"
                      : "#F8FAFC"
                    : isDark
                    ? "#251C36"
                    : "#EFF6FF",
                  border: isRead
                    ? isDark
                      ? "1px solid rgba(255,255,255,0.06)"
                      : "1px solid #E2E8F0"
                    : isCritical(type)
                    ? "1px solid #FCA5A5"
                    : isDark
                    ? "1px solid rgba(37,99,235,0.4)"
                    : "1px solid #BFDBFE",
                }}
              >
                <div style={{ paddingTop: 2 }}>{getIcon(type)}</div>

                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      gap: 8,
                    }}
                  >
                    <Typography.Text
                      strong
                      style={{
                        fontSize: 13,
                        color: isRead
                          ? isDark
                            ? "#94A3B8"
                            : "#475569"
                          : isDark
                          ? "#FFFFFF"
                          : "#1E293B",
                      }}
                    >
                      {item.title ?? item.Title}
                    </Typography.Text>
                    <Tag
                      color={isCritical(type) ? "error" : "warning"}
                      style={{ margin: 0, fontSize: 10, flexShrink: 0 }}
                    >
                      {timeStr}
                    </Tag>
                  </div>

                  <div
                    style={{
                      fontSize: 12,
                      color: isRead ? "#64748B" : isDark ? "#CBD5E1" : "#334155",
                      margin: "4px 0 8px 0",
                    }}
                  >
                    {item.message ?? item.Message}
                  </div>

                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    {linkUrl ? (
                      <span
                        style={{
                          fontSize: 12,
                          fontWeight: 600,
                          color: "#2563EB",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 4,
                        }}
                      >
                        <ExternalLink size={12} />
                        {DEBT_TYPES.includes(type) ? t("goToClientCard") : t("goToStock")}
                      </span>
                    ) : (
                      <div />
                    )}

                    {!isRead && (
                      <Button
                        type="text"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation()
                          markReadMutation(id)
                        }}
                        style={{ fontSize: 11, color: "#64748B" }}
                      >
                        {t("closeBtn")}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            )
          }}
        />
      )}
    </Modal>
  )
}
